import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldAlert, Clock, ArrowRight } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import ErrorBoundary from './ErrorBoundary';
import { Button } from '@/components/ui/button';
import ChatAssistant from './ChatAssistant';
import { trackScrollDepth, trackButtonClick } from '@/src/lib/analytics';

export default function Layout() {
  const location = useLocation();
  const [showSticky, setShowSticky] = useState(false);
  const [scrolledDepths, setScrolledDepths] = useState<number[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setShowSticky(window.scrollY > 600);

      // Scroll depth tracking
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      const percentage = Math.round((currentScroll / scrollHeight) * 100);

      const milestones = [25, 50, 75, 90];
      milestones.forEach(milestone => {
        if (percentage >= milestone && !scrolledDepths.includes(milestone)) {
          trackScrollDepth(milestone);
          setScrolledDepths(prev => [...prev, milestone]);
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolledDepths]);

  // Reset scroll depths on page change
  useEffect(() => {
    setScrolledDepths([]);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background selection:bg-accent/30 selection:text-accent relative">
      <div className="noise" />
      <Navbar />
      <main className="flex-grow pt-20">
        <ErrorBoundary>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </ErrorBoundary>
      </main>

      {/* Sticky CTA */}
      <AnimatePresence>
        {showSticky && location.pathname !== '/contact' && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-28 right-8 z-50 flex flex-col items-end gap-4"
          >
            <div className="bg-background/80 backdrop-blur-xl border border-accent/30 p-4 rounded-2xl shadow-2xl flex items-center gap-4 max-w-xs">
              <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent shrink-0">
                <Clock className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-accent uppercase tracking-widest">Limited Availability</p>
                <p className="text-xs text-muted-foreground font-medium">Only 3 consultation slots left this week.</p>
              </div>
            </div>
            <Button 
              asChild 
              size="lg" 
              className="h-16 px-8 rounded-full bg-accent hover:bg-accent/90 text-background font-bold shadow-[0_0_40px_rgba(0,150,255,0.4)] group"
              onClick={() => trackButtonClick('Book Free Security Audit', 'Sticky CTA')}
            >
              <Link to="/audit" className="flex items-center gap-3">
                Book Free Security Audit
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <ChatAssistant />
      <Footer />
    </div>
  );
}
